import {
  AnyPromotionInfo,
  Feature,
  findPromotionForProduct,
  getProductId,
  isEcomFeature,
  PlanInterval,
  Subscription,
  SubscriptionPlanPrice,
  toEcomPlanCode,
} from '../../models';
import { EcomEligibility } from '../../services/subscription';
import { FeatureQuotaAndCycleMap } from '../../hook/useGetQuotaAndCycleFromPathParams';
import { getUnitPrice } from '../../hook/useCalculatePrice';
import { getTargetSelfServeSubscription } from '../../utils/subscriptionTarget';
import { gateCampaignForTrueBundleCart } from '../plan/component/true-platform/trueBundlePreview';
import { getStarterPackageOffer } from '../plan/component/true-platform/starterPackageCampaign';
import {
  KSE_TESTING_PLAN_OPTION,
  ProductIconKey,
  TRUE_TESTING_PLAN_OPTIONS,
  TrueTestingPlanOption,
} from '../plan/component/true-platform/trueTestingProducts';

/** Katalon Studio's tiered Starter offer on the annual cycle ("first 3 at $1,000, then $1,800"). */
export interface AddProductsTier {
  count: number;
  thenPrice: number;
}

/** One catalogue row of the checkout's "Add products" / "Edit order" dialog. */
export interface AddProductsRow {
  feature: Feature;
  titleId: string;
  unit: TrueTestingPlanOption['unit'];
  iconKey: ProductIconKey;
  /** Quantity already in this order (0 = not in the order). */
  inOrderQuantity: number;
  /** Billing cycle of the in-order line; the cycle a new line starts on otherwise. */
  interval: PlanInterval;
  /** The product can only bill annually here (an annual paid subscription already exists). */
  intervalLocked: boolean;
  /** Set when the product cannot join this order (the dialog disables its "Add"). */
  blockedHint?: string;
  /** Unit price per billing cycle, the way the plan page shows it. */
  unitPrices: Record<'YEAR' | 'MONTH', number>;
  /** Annual tier of a tiered Starter offer: `unitPrices.YEAR` is the discounted first-tier rate. */
  tier?: AddProductsTier;
}

export interface AddProductsRowsInput {
  featuresQuotaAndCycle: FeatureQuotaAndCycleMap;
  /** The account's Recurly subscriptions (self-serve and direct-sale). */
  recurlySubscriptions: Subscription[];
  targetSubscriptionId?: number;
  /**
   * A Starter bundle is in this order: no other campaign applies (1 order = 1 campaign), so Katalon
   * Studio joins at list price here - the "Add more products" flow is the one place a Starter
   * package order takes Studio (product decision 2026-10-14; the plan page still keeps it out).
   */
  isBundleInCart: boolean;
  planPrice?: SubscriptionPlanPrice;
  activePromotion?: AnyPromotionInfo | null;
  activeCampaigns?: AnyPromotionInfo[];
  ecomEligibility: EcomEligibility;
}

/**
 * Design order of the dialog: Katalon Studio first, then the "More testing plans" rows in the
 * order the plan page lists them (TestOps, Runtime Engine, the three TestCloud sessions).
 */
export const ADD_PRODUCTS_CATALOGUE: TrueTestingPlanOption[] = [
  KSE_TESTING_PLAN_OPTION,
  ...TRUE_TESTING_PLAN_OPTIONS,
];

/** Hard cap of the dialog's quantity stepper (design: 500) — same cap as the Order Summary rows. */
export { ORDER_SUMMARY_MAX_QUANTITY as ADD_PRODUCTS_MAX_QUANTITY } from '../plan/component/true-platform/OrderSummaryLineItems';

/**
 * TestOps is sold online only once its LaunchDarkly price key is seeded on the env, mirroring the
 * plan page's `testOpsRowEnabled` (plan Decision 8: the price key is the per-env rollout switch).
 */
export const isAddProductsFeatureOnSale = (
  feature: Feature,
  planPrice?: SubscriptionPlanPrice,
): boolean => feature !== Feature.TESTOPS_G3
  || planPrice?.testOpsG3Annually !== undefined
  || planPrice?.testOpsG3Monthly !== undefined;

/**
 * The billing cycle every product already in the order agrees on, or undefined when the order is
 * empty or mixes monthly and annual lines.
 */
const getUniformOrderInterval = (
  featuresQuotaAndCycle: FeatureQuotaAndCycleMap,
): PlanInterval | undefined => {
  const intervals = ADD_PRODUCTS_CATALOGUE
    .map(option => featuresQuotaAndCycle[option.feature])
    .filter(entry => (entry?.quota ?? 0) > 0)
    .map(entry => entry?.cycle ?? PlanInterval.YEAR);
  if (intervals.length === 0) {
    return undefined;
  }
  return intervals.every(interval => interval === intervals[0]) ? intervals[0] : undefined;
};

/**
 * Builds the dialog rows from the same inputs the order confirmation page prices its preview
 * with, so a product added here lands in the Order Summary with the price the row advertised.
 */
export const buildAddProductsRows = (
  {
    featuresQuotaAndCycle,
    recurlySubscriptions,
    targetSubscriptionId,
    isBundleInCart,
    planPrice,
    activePromotion,
    activeCampaigns,
    ecomEligibility,
  }: AddProductsRowsInput,
): AddProductsRow[] => {
  const uniformInterval = getUniformOrderInterval(featuresQuotaAndCycle);

  return ADD_PRODUCTS_CATALOGUE
    .filter(option => isAddProductsFeatureOnSale(option.feature, planPrice))
    .map(option => {
      const { feature } = option;
      const entry = featuresQuotaAndCycle[feature];
      const inOrderQuantity = entry?.quota ?? 0;
      const existingSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      // Same precedence as the plan page row: the URL cycle param, then the existing self-serve
      // subscription's cycle, then the cycle the rest of the order is on, then annual.
      const interval = entry?.cycle
        ?? existingSubscription?.billingInterval
        ?? uniformInterval
        ?? PlanInterval.YEAR;
      const intervalLocked = recurlySubscriptions.some(
        subscription => subscription.feature === feature
          && subscription.billingInterval === PlanInterval.YEAR,
      );
      const annualPrice = getUnitPrice(planPrice, feature, PlanInterval.YEAR, 'product');
      const monthlyPrice = getUnitPrice(planPrice, feature, PlanInterval.MONTH, 'product');

      // Tiered Starter offer (KS Starter: first 3 licenses at $1,000/yr): matched on the exact
      // plan code the BE charges - the ecom code for an eligible account - and gated off while a
      // bundle is in the cart (1 order = 1 campaign), exactly as the plan page row prices itself.
      const annualCode = `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(PlanInterval.YEAR)}`;
      const chargedCode = isEcomFeature(feature) && ecomEligibility[feature]
        ? toEcomPlanCode(annualCode)
        : annualCode;
      const campaign = gateCampaignForTrueBundleCart(
        findPromotionForProduct(activeCampaigns, activePromotion, chargedCode),
        chargedCode,
        isBundleInCart,
      );
      const offer = getStarterPackageOffer(campaign, chargedCode, isEcomFeature(feature));
      const tierPrice = offer?.kind === 'tiered' ? annualPrice - offer.perUnitDiscount : 0;
      const tier = offer?.kind === 'tiered' && tierPrice > 0 && tierPrice < annualPrice
        ? { count: offer.seatCount, thenPrice: annualPrice }
        : undefined;

      return {
        feature,
        titleId: option.titleId,
        unit: option.unit,
        iconKey: option.iconKey,
        inOrderQuantity,
        interval,
        intervalLocked,
        unitPrices: {
          YEAR: tier ? tierPrice : annualPrice,
          MONTH: monthlyPrice,
        },
        ...(tier ? { tier } : {}),
      };
    });
};

/** Price of `quantity` units of a row on a cycle, honouring the tier of a tiered Starter offer. */
export const getAddProductsLinePrice = (
  row: AddProductsRow,
  quantity: number,
  interval: PlanInterval,
): number => {
  if (quantity <= 0) {
    return 0;
  }
  if (interval === PlanInterval.YEAR && row.tier) {
    const tieredQuantity = Math.min(quantity, row.tier.count);
    return tieredQuantity * row.unitPrices.YEAR
      + (quantity - tieredQuantity) * row.tier.thenPrice;
  }
  return quantity * (interval === PlanInterval.YEAR ? row.unitPrices.YEAR : row.unitPrices.MONTH);
};
